import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactElement } from 'react';
import * as RS from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Select primitive (Compose API over Radix UI).
 *
 * Usage:
 * ```tsx
 * <Select value={v} onValueChange={setV}>
 *   <SelectTrigger><SelectValue placeholder="Pick…" /></SelectTrigger>
 *   <SelectContent>
 *     <SelectItem value="a">Option A</SelectItem>
 *     <SelectItem value="b">Option B</SelectItem>
 *   </SelectContent>
 * </Select>
 * ```
 */
export const Select = RS.Root;
export const SelectGroup = RS.Group;
export const SelectValue = RS.Value;

export const SelectTrigger = forwardRef<
  ElementRef<typeof RS.Trigger>,
  ComponentPropsWithoutRef<typeof RS.Trigger>
>(function SelectTrigger({ className, children, ...props }, ref) {
  return (
    <RS.Trigger
      ref={ref}
      className={cn(
        'flex h-10 w-full items-center justify-between gap-2 rounded-card border border-border ' +
          'bg-bg-input px-3 text-sm text-white placeholder:text-muted ' +
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ' +
          'focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:cursor-not-allowed ' +
          'disabled:opacity-60 [&>span]:line-clamp-1',
        className,
      )}
      {...props}
    >
      {children}
      <RS.Icon asChild>
        <ChevronDown size={16} className="text-muted shrink-0" aria-hidden />
      </RS.Icon>
    </RS.Trigger>
  );
});

export const SelectContent = forwardRef<
  ElementRef<typeof RS.Content>,
  ComponentPropsWithoutRef<typeof RS.Content>
>(function SelectContent({ className, children, position = 'popper', sideOffset = 4, ...props }, ref) {
  return (
    <RS.Portal>
      <RS.Content
        ref={ref}
        position={position}
        sideOffset={sideOffset}
        className={cn(
          'z-50 max-h-[--radix-select-content-available-height] overflow-hidden rounded-card ' +
            'border border-border bg-bg-card text-white shadow-2xl animate-fade-in ' +
            'min-w-[--radix-select-trigger-width]',
          className,
        )}
        {...props}
      >
        <RS.ScrollUpButton className="flex h-6 cursor-default items-center justify-center bg-bg-card">
          <ChevronUp size={14} aria-hidden />
        </RS.ScrollUpButton>
        <RS.Viewport className="p-1">{children}</RS.Viewport>
        <RS.ScrollDownButton className="flex h-6 cursor-default items-center justify-center bg-bg-card">
          <ChevronDown size={14} aria-hidden />
        </RS.ScrollDownButton>
      </RS.Content>
    </RS.Portal>
  );
});

export const SelectLabel = forwardRef<
  ElementRef<typeof RS.Label>,
  ComponentPropsWithoutRef<typeof RS.Label>
>(function SelectLabel({ className, ...props }, ref) {
  return <RS.Label ref={ref} className={cn('px-2 py-1.5 text-xs text-muted', className)} {...props} />;
});

export const SelectItem = forwardRef<
  ElementRef<typeof RS.Item>,
  ComponentPropsWithoutRef<typeof RS.Item>
>(function SelectItem({ className, children, ...props }, ref): ReactElement {
  return (
    <RS.Item
      ref={ref}
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 ' +
          'text-sm text-white outline-none data-[highlighted]:bg-bg-hover ' +
          'data-[disabled]:pointer-events-none data-[disabled]:opacity-50 ' +
          'data-[state=checked]:text-primary',
        className,
      )}
      {...props}
    >
      <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
        <RS.ItemIndicator>
          <Check size={14} aria-hidden />
        </RS.ItemIndicator>
      </span>
      <RS.ItemText>{children}</RS.ItemText>
    </RS.Item>
  );
});

export const SelectSeparator = forwardRef<
  ElementRef<typeof RS.Separator>,
  ComponentPropsWithoutRef<typeof RS.Separator>
>(function SelectSeparator({ className, ...props }, ref) {
  return <RS.Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />;
});
